import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  financeService,
  Invoice,
  Payment,
  FinancialSummary,
} from '../../services/finance.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

const INVOICE_STATUSES = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'sent', label: 'Enviada' },
  { value: 'paid', label: 'Pagada' },
  { value: 'partial', label: 'Parcial' },
  { value: 'overdue', label: 'Vencida' },
  { value: 'cancelled', label: 'Cancelada' },
];

const PAYMENT_METHODS = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'check', label: 'Cheque' },
];

const fc = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);

const fd = (ts: number) =>
  new Date(ts).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export function FinancePage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'invoices' | 'payments'>('invoices');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showPaymentForm, setShowPaymentForm] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  const canRegisterPayment = ['sys_admin', 'general_manager', 'accountant'].includes(
    user?.role ?? ''
  );

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      financeService.getFinancialSummary().catch(() => null),
      financeService.getInvoices().catch(() => [] as Invoice[]),
      financeService.getPayments().catch(() => [] as Payment[]),
    ])
      .then(([s, inv, pay]) => {
        setSummary(s);
        setInvoices(inv);
        setPayments(pay);
      })
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filteredInvoices = useMemo(() => {
    if (filterStatus === 'all') return invoices;
    return invoices.filter((i) => i.status === filterStatus);
  }, [invoices, filterStatus]);

  const openPaymentForm = (invoice: Invoice) => {
    const paid = payments
      .filter((p) => p.invoice_id === invoice.id)
      .reduce((s, p) => s + p.amount, 0);
    const pending = Math.max(invoice.total - paid, 0);
    setPaymentAmount(pending.toFixed(2));
    setPaymentMethod('transfer');
    setPaymentReference('');
    setPaymentNotes('');
    setShowPaymentForm(invoice);
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showPaymentForm) return;
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      showError('Monto inválido');
      return;
    }
    setSavingPayment(true);
    try {
      await financeService.createPayment({
        invoice_id: showPaymentForm.id,
        amount,
        method: paymentMethod as Payment['method'],
        reference: paymentReference || undefined,
        date_paid: Date.now(),
        notes: paymentNotes || undefined,
      });
      showSuccess('Pago registrado');
      setShowPaymentForm(null);
      load();
    } catch (err: any) {
      showError(err.message || 'Error al registrar');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleStatusChange = async (invoice: Invoice, status: string) => {
    try {
      await financeService.updateInvoiceStatus(invoice.id, status as Invoice['status']);
      showSuccess('Estado actualizado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  if (loading && !summary) return <LoadingState message="Cargando finanzas…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Finanzas</h1>
        <p className="text-xs text-slate-500">Facturas, pagos y resumen</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Ingresos</p>
            <p className="text-base font-bold text-emerald-600">
              {fc(summary.totalRevenue)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Utilidad neta</p>
            <p className="text-base font-bold text-primary">{fc(summary.netProfit)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Pendientes</p>
            <p className="text-base font-bold text-amber-600">
              {summary.pendingInvoices}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Vencidas</p>
            <p className="text-base font-bold text-red-600">
              {summary.overdueInvoices}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
        <button
          onClick={() => setActiveTab('invoices')}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${
            activeTab === 'invoices'
              ? 'bg-primary text-white'
              : 'text-slate-600'
          }`}
        >
          Facturas ({invoices.length})
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${
            activeTab === 'payments'
              ? 'bg-primary text-white'
              : 'text-slate-600'
          }`}
        >
          Pagos ({payments.length})
        </button>
      </div>

      {activeTab === 'invoices' && (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                filterStatus === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              Todas
            </button>
            {INVOICE_STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => setFilterStatus(s.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                  filterStatus === s.value
                    ? 'bg-primary text-white'
                    : 'bg-white text-slate-600 border border-slate-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {filteredInvoices.length === 0 ? (
            <EmptyState
              icon="🧾"
              title={invoices.length === 0 ? 'Sin facturas' : 'Sin resultados'}
              description="Cuando existan facturas aparecerán aquí."
            />
          ) : (
            <ul className="space-y-2">
              {filteredInvoices.map((inv) => {
                const paid = payments
                  .filter((p) => p.invoice_id === inv.id)
                  .reduce((s, p) => s + p.amount, 0);
                const pending = Math.max(inv.total - paid, 0);
                const canPay = inv.status !== 'paid' && inv.status !== 'cancelled';
                return (
                  <li key={inv.id} className="bg-white rounded-xl shadow-sm p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <p className="text-[11px] font-mono text-slate-500">
                          {inv.number}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {inv.client_name || inv.project_name || '—'}
                        </p>
                      </div>
                      <StatusBadge status={inv.status} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                      <span className="text-slate-500">Total: {fc(inv.total)}</span>
                      {pending > 0 && (
                        <span className="text-amber-600 font-semibold">
                          Pendiente: {fc(pending)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5 pt-1.5 border-t border-slate-100">
                      <select
                        value={inv.status}
                        onChange={(e) => handleStatusChange(inv, e.target.value)}
                        className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 text-xs"
                      >
                        {INVOICE_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {canRegisterPayment && canPay && (
                        <button
                          onClick={() => openPaymentForm(inv)}
                          className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold"
                        >
                          + Pago
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {activeTab === 'payments' && (
        <>
          {payments.length === 0 ? (
            <EmptyState icon="💳" title="Sin pagos" description="Aún no hay pagos registrados." />
          ) : (
            <ul className="space-y-2">
              {payments.slice(0, 30).map((p) => (
                <li key={p.id} className="bg-white rounded-xl shadow-sm p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        {fc(p.amount)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {p.invoice_number ?? p.invoice_id.slice(0, 8)} ·{' '}
                        {PAYMENT_METHODS.find((m) => m.value === p.method)?.label ??
                          p.method}
                      </p>
                      {p.reference && (
                        <p className="text-[11px] text-slate-400 truncate">
                          Ref: {p.reference}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                      {fd(p.date_paid)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showPaymentForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Registrar pago</h2>
              <button
                onClick={() => setShowPaymentForm(null)}
                className="text-slate-400 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 mb-3 text-xs">
              <p className="text-slate-500">Factura</p>
              <p className="font-semibold text-slate-900">
                {showPaymentForm.number} — {fc(showPaymentForm.total)}
              </p>
            </div>
            <form onSubmit={handleSubmitPayment} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Monto *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Método
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Referencia
                </label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Folio o # de transferencia"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Notas
                </label>
                <textarea
                  rows={2}
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPaymentForm(null)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingPayment}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {savingPayment ? 'Guardando…' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}